import { Dialog } from '@headlessui/react';
import { CheckCircle, Mail, ArrowRight, X, RefreshCw } from 'lucide-react';
import Button from './Button';
import { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
import { resendVerificationEmail, isEmailVerified } from '@/lib/firebase/auth';
import { createFreeSubscription } from '@/lib/firebase/membership/queries';
import { useAuth } from '@/components/AuthProvider';
import { toast } from 'sonner';
import { useTranslation, Trans } from 'react-i18next';

interface OnboardingModalProps {
    isOpen: boolean;
    onClose: () => void;
    userEmail: string;
    creatorName?: string;
    creatorId?: string;
    creatorPath?: string; // Friendly URL path for redirect
    onContinue: () => void;
}

export default function OnboardingModal({
    isOpen,
    onClose,
    userEmail,
    creatorName = 'this athlete',
    creatorId,
    creatorPath,
    onContinue
}: OnboardingModalProps) {
    const { user } = useAuth();
    // const navigate = useNavigate();
    const creatorIdentifier = creatorPath || creatorId;
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const [emailResent, setEmailResent] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [isCheckingVerification, setIsCheckingVerification] = useState(false);
    const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);

    const handleResendEmail = async () => {
        setIsResending(true);
        try {
            await resendVerificationEmail();
            setEmailResent(true);
            toast.success(t('toasts.verificationSent'));
        } catch (error: any) {
            toast.error(error.message || t('toasts.verificationFailed'));
        } finally {
            setIsResending(false);
        }
    };

    const handleContinue = async () => {
        setIsCheckingVerification(true);
        try {
            const verified = await isEmailVerified();
            if (verified) {
                // Create the free subscription now that email is verified
                if (creatorId && user?.uid) {
                    setIsCreatingSubscription(true);
                    try {
                        await createFreeSubscription(user.uid, creatorId);
                        toast.success(t('toasts.subscriptionSuccess'));
                    } catch (error: any) {
                        console.error('Error creating subscription:', error);
                        // Don't show error toast here as the user has already verified their email
                    } finally {
                        setIsCreatingSubscription(false);
                    }
                }

                setStep(2);
                toast.success(t('toasts.emailVerified'));
            } else {
                toast.error(t('toasts.verifyFirst'));
            }
        } catch (error: any) {
            toast.error(t('toasts.checkFailed'));
        } finally {
            setIsCheckingVerification(false);
        }
    };

    const handleFinish = () => {
        onContinue();
        onClose();

        // Redirect to dashboard fund view with page refresh
        if (creatorIdentifier) {
            // Use window.location to force a page refresh
            window.location.href = `/dashboard/fund/${creatorIdentifier}`;
        }
    };

    const handleClose = () => {
        onClose();

        // If user is on success step (step 2), redirect to dashboard fund view
        if (step === 2 && creatorIdentifier) {
            // Use window.location to force a page refresh
            window.location.href = `/dashboard/fund/${creatorIdentifier}`;
        }
    };

    // Reset step when modal opens
    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setEmailResent(false);
        }
    }, [isOpen]);

    return (
        <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
            <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="mx-auto max-w-md w-full rounded-xl bg-white p-6">
                    <div className="flex items-center justify-between mb-6">
                        <Dialog.Title className="text-xl font-semibold flex items-center gap-2">
                            {t('onboarding.title')}
                        </Dialog.Title>
                        <button onClick={handleClose} className="text-gray-400 hover:text-gray-500">
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {step === 1 ? (
                        <div className="space-y-6">
                            <div className="text-center">
                                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                    <Mail className="h-8 w-8 text-green-600" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    {t('onboarding.verifyTitle')}
                                </h3>
                                <p className="text-gray-600 mb-4">
                                    <Trans i18nKey="onboarding.sentTo" values={{ email: userEmail }} components={{ 0: <strong /> }} />
                                </p>
                                <p className="text-sm text-gray-500">
                                    {t('onboarding.instructions', { creatorName })}
                                </p>
                            </div>

                            <div className="flex flex-col gap-3">
                                <Button
                                    onClick={handleContinue}
                                    disabled={isCheckingVerification || isCreatingSubscription}
                                    className="w-full bg-primary hover:bg-primary/90 text-white"
                                >
                                    {isCheckingVerification ? (
                                        <>
                                            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                                            {t('onboarding.checking')}
                                        </>
                                    ) : isCreatingSubscription ? (
                                        <>
                                            <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                                            {t('onboarding.settingUp')}
                                        </>
                                    ) : (
                                        <>
                                            {t('onboarding.verifiedCta')}
                                            <ArrowRight className="h-4 w-4 ml-2" />
                                        </>
                                    )}
                                </Button>

                                <div className="text-center">
                                    <p className="text-sm text-gray-500 mb-2">{t('onboarding.noEmailHeading')}</p>
                                    <button
                                        onClick={handleResendEmail}
                                        disabled={isResending}
                                        className="text-sm text-primary hover:text-primary/90 disabled:opacity-50 flex items-center gap-1 mx-auto"
                                    >
                                        {isResending ? (
                                            <>
                                                <RefreshCw className="h-3 w-3 animate-spin" />
                                                {t('actions.check')}
                                            </>
                                        ) : (
                                            <>
                                                <Mail className="h-3 w-3" />
                                                {t('onboarding.resend')}
                                            </>
                                        )}
                                    </button>
                                    {emailResent && (
                                        <p className="text-xs text-green-600 mt-1">{t('onboarding.resent')}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div className="text-center">
                                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                                    <CheckCircle className="h-8 w-8 text-green-600" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    {t('onboarding.successTitle')}
                                </h3>
                                <p className="text-gray-600 mb-4">
                                    {t('onboarding.successDesc', { creatorName })}
                                </p>
                            </div>

                            <div className="bg-green-50 rounded-lg p-4">
                                <h4 className="font-medium text-green-900 mb-2">{t('onboarding.whatNow')}</h4>
                                <ul className="text-sm text-green-800 space-y-1">
                                    <li>{t('onboarding.whatNow1')}</li>
                                    <li>{t('onboarding.whatNow2')}</li>
                                    <li>{t('onboarding.whatNow3')}</li>
                                    <li>{t('onboarding.whatNow4')}</li>
                                </ul>
                            </div>

                            <div className="flex flex-col gap-3">
                                <Button
                                    onClick={handleFinish}
                                    className="w-full bg-primary hover:bg-primary/90 text-white"
                                >
                                    {t('actions.startExploring')}
                                    <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>

                                <button
                                    onClick={() => setStep(1)}
                                    className="text-sm text-gray-500 hover:text-gray-700"
                                >
                                    {t('onboarding.backToVerification')}
                                </button>
                            </div>
                        </div>
                    )}
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}
